import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import moment from 'moment';

const MAX_AMOUNT = '999999999999.99';

const VALID_RULES = [
  'required',
  'number',
  'date',
  'required_if',
  'tin_number',
  'mobile_number',
  'telephone_number',
  'array',
  'object',
  'rptax_tdn',
  'rptax_pin',
  'rptax_pin_short',
  'tax_payer_account_number',
  'btax_bin',
  'amount',
  'email',
  'min_six',
  'cedula_serial_number',
  // 'cedula_serial_number_with_hypen',
  'dti_no',
  'sec_no',
  'cda_no',
  'max_30_char',
  'max_12_digits',
  'cellphone_number',
  'cellphone_number_+639',
  'tel_number',
  'tin_no',
  'amount_zero',
  'less_than_1',
  'text_underscore',
  'password',
  'match',
  'pin',
  'mask',
  'tdn_no',
  'pin_no',
  'min_3_digits',
  'min_5_char',
  'max_20_char',
  'max_20_alphanumeric',
  'min_5_alphanumeric',
  'min_3_character',
  'ovr_number',
  'driver_license_number',
  'zip_code',
  'apprehended_at_before',
  'current_year',
  'mobile_number_63',
  'max_2_char',
  'rptax_pin_23'
];

const ucWords = (value) => {
  return value
    .replace('_code', '')
    .replace('_id', '')
    .replace(/_+/g, ' ')
    .split(/\s/)
    .map((w) => {
      const [first, ...rest] = w;

      return `${first.toUpperCase()}${rest.join('')}`;
    })
    .join(' ');
};

export default (data = {}, init_object_rules = {}, message_settings = {}) => {
  const [response, setResponse] = useState({});

  const [object_rules, setObjectRules] = useState(init_object_rules);

  // rerender iterated/set object on state update
  const onSetObjectRules = (newRules) => {
    setObjectRules(newRules);
  };

  useEffect(() => {
    // eslint-disable-next-line no-use-before-define
    validate();
    // eslint-disable-next-line
  }, [Object.values(data).join(','), object_rules]);

  if (Array.isArray(data)) {
    throw new Error(
      `Invalid data argument type should be object, received an array`
    );
  }

  if (typeof data !== 'object') {
    throw new Error(
      `Invalid data argument type should be object, received an ${typeof data}`
    );
  }

  if (Array.isArray(object_rules)) {
    throw new Error(
      `Invalid object_rules argument type should be object, received an array`
    );
  }

  if (typeof object_rules !== 'object') {
    throw new Error(
      `Invalid object_rules argument type should be object, received an ${typeof object_rules}`
    );
  }

  if (Array.isArray(message_settings)) {
    throw new Error(
      `Invalid message_settings argument type should be object, received an array`
    );
  }

  if (typeof message_settings !== 'object') {
    throw new Error(
      `Invalid message_settings argument type should be object, received an ${typeof message_settings}`
    );
  }

  const ruleEntries = Object.entries(object_rules);

  const validateRule = (field, rule_pattern) => {
    const fieldUCWords = ucWords(field);

    const value = data[field];

    const [rule, args] = rule_pattern.split(/:/);

    if (VALID_RULES.indexOf(rule) < 0) {
      throw new Error(`${rule} is not valid rule`);
    }

    const fieldOnMessage =
      message_settings[field] && message_settings[field][rule]
        ? message_settings[field][rule]
        : fieldUCWords;

    if (rule === 'required') {
      if (value === undefined) {
        return {
          field,
          is_passed: false,
          generic_message: 'Required',
          message: `${fieldOnMessage} field is Required`,
        };
      }

      if (typeof value === 'string' && !value.toString().trim()) {
        return {
          field,
          is_passed: false,
          generic_message: 'Required',
          message: `${fieldOnMessage} field is Required`,
        };
      }

      if (typeof value === 'number' && value === '') {
        return {
          field,
          is_passed: false,
          generic_message: 'Required',
          message: `${fieldOnMessage} field is Required`,
        };
      }

      if (value === false) {
        return {
          field,
          is_passed: false,
          generic_message: 'Required',
          message: `${fieldOnMessage} field is Required`,
        };
      }

      if (Array.isArray(value) && value.length === 0) {
        return {
          field,
          is_passed: false,
          generic_message: 'Required',
          message: `${fieldOnMessage} field is Empty`,
        };
      }
    }

    if (rule === 'number') {
      if (value && !/^\d{1,}(\.\d*)?$/.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Must be a Number',
          message: `${fieldOnMessage} field is invalid format, should be number`,
        };
      }
    }

    if (rule === 'email') {
      if (
        value &&
        !/^[\w+]+([.-]?[\w+]+)*@[\w+]+([.-]?[\w+]+)*(\.\w{2,3})+$/.test(value)
      ) {
        return {
          field,
          is_passed: false,
          generic_message: 'Must be a valid Email',
          message: `${fieldOnMessage} is Invalid`,
        };
      }
    }

    if (rule === 'amount') {
      if (value && +value > MAX_AMOUNT) {
        return {
          field,
          is_passed: false,
          generic_message: `Invalid Amount, should be between 1.00 to ${parseFloat(
            MAX_AMOUNT
          ).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
          message: `${fieldOnMessage} field is invalid amount, should be between 1.00 to ${parseFloat(
            MAX_AMOUNT
          ).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
        };
      }
    }

    if (rule === 'rptax_tdn') {
      if (value && !/^\w{2,2}-\d{5,5}-\d{5,5}$/.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid format, should be AA-00000-00000',
          message: `${fieldOnMessage} field is invalid format, should be AA-00000-00000`,
        };
      }
    }

    if (rule === 'rptax_pin') {
      if (
        value &&
        !/^\d{3,3}-\d{2,2}-\d{3,3}-\d{3,3}-\d{3,3}-[\w\d]{5,5}$/.test(value)
      ) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid format, should be 000-00-000-000-000-A0000',
          message: `${fieldOnMessage} field is invalid format, should be 000-00-000-000-000-A0000`,
        };
      }
    }

    if (rule === "rptax_pin_23") {
      if (
        value &&
        !/^\d{3,3}-\d{2,2}-\d{3,3}-\d{3,3}-\d{3,3}-[\w\d]{5,5}-\d{4,4}$/.test(value)
      ) {
        return {
          field,
          is_passed: false,
          generic_message: "Invalid format, should be 000-00-000-000-000-A0000-0000",
          message: `${fieldOnMessage} field is invalid format, should be 000-00-000-000-000-A0000-0000`,
        };
      }
    }

    if (rule === 'min_six') {
      if (value && !/^.{6,}$/.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid format, should be minimum of 6 characters',
          message: `${fieldOnMessage} field is invalid format, id length must be greater that 6 digits`,
        };
      }
    }

    if (rule === 'rptax_pin_short') {
      if (value && !/^\d{3,3}-\d{2,2}-\d{3,3}-\d{3,3}-\d{3,3}$/.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid format, should be 000-00-000-000-000',
          message: `${fieldOnMessage} field is invalid format, should be 000-00-000-000-000`,
        };
      }
    }

    if (rule === 'tax_payer_account_number') {
      if (value && !/^\d{3,3}-\d{3,3}-\d{3,3}-\d{5,5}$/.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid format, should be 000-000-000-00000',
          message: `${fieldOnMessage} field is invalid format, should be 000-000-000-00000`,
        };
      }
    }

    if (rule === 'btax_bin') {
      if (value && !/^\d{3,3}-\d{2,2}-\d{4,4}-\d{7,7}$/.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid format, should be 000-00-0000-0000000',
          message: `${fieldOnMessage} field is invalid format, should be 000-00-0000-0000000`,
        };
      }
    }

    if (rule === 'cedula_serial_number') {
      if (value && !/^\d{1,8}$/.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid format',
          message: `${fieldOnMessage} field is invalid format`,
        };
      }
    }

    if (rule === 'apprehended_at_before') {
      if (new Date(value) > new Date()) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid Date. Date should be before today.',
          message: `${fieldOnMessage} field is invalid date, should not beyond today.`,
        };
      }
    }

    if (rule === 'current_year') {
      if (
        `${moment(value).format('YYYY')}` <
        `${moment(new Date()).format('YYYY')}`
      ) {
        return {
          field,
          is_passed: false,
          generic_message:
            'Invalid Date. Date should be within this year only.',
          message: `${fieldOnMessage} field is invalid date, should be within this year only.`,
        };
      }
    }

    if (rule === 'mobile_number_63') {
      const regex = new RegExp(/^639\d{9,9}$/);

      if (value && !regex.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: `Invalid format, should be 639000000000`,
          message: `${fieldOnMessage} field is invalid format, should be 639000000000`,
        };
      }
    }

    //  availabel in cms
    // if (rule === 'cedula_serial_number_with_hypen') {
    //   if (value && /^[0-8 -]+$/.test(value)) {
    //     return {
    //       field,
    //       is_passed: false,
    //       generic_message: 'Invalid format',
    //       message: `${fieldOnMessage} field is invalid format`,
    //     };
    //   }
    // }

    if (rule === 'array') {
      if (value && !Array.isArray(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Must be a List',
          message: `${fieldOnMessage} field is invalid format, should be List`,
        };
      }
    }

    if (rule === 'object') {
      const isObj = (o) => o?.constructor === Object;
      if (!isObj) {
        return {
          field,
          is_passed: false,
          generic_message: 'Must be an object',
          message: `${fieldOnMessage} field is invalid format, should be an Object`,
        };
      }
    }

    if (rule === 'date') {
      if (value && !/^\d{2,2}\/\d{2,2}\/\d{4,4}$/.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Must be a Date (MM/DD/YYYY)',
          message: `${fieldOnMessage} field is invalid format, should be MM/DD/YYYY`,
        };
      }
    }

    if (rule === 'required_if') {
      // format fieldconditionvalue sample field=OTHER
      const fieldConditionValue = new RegExp(/^\w+(=|>|<|!=|~){1,1}[\w\W]+$/);
      if (!fieldConditionValue.test(args)) {
        throw new Error(
          'Invalid required_if rules argument, sample target_field=value or target_field!=value'
        );
      }

      const [targetField, targetCondition, targetValue] =
        args.split(/(=|>|<|!=|~){1,1}/);

      const fieldValue = data[targetField];
      const fieldValueTypeIsString = typeof fieldValue === 'string';
      const fieldValueTypeIsNumber = typeof fieldValue === 'number';
      const targetFieldUCWords = ucWords(targetField);

      const targetFieldUCWordsOnMessage =
        message_settings[targetField] && message_settings[targetField].required
          ? message_settings[targetField].required
          : targetFieldUCWords;

      const targetValueOnMessage =
        message_settings[targetField] &&
        message_settings[targetField].required_if_value
          ? message_settings[targetField].required_if_value
          : targetValue;

      const customMessage = message_settings[field]?.generic_message
        ? message_settings[field].generic_message
        : undefined;

      if (
        fieldValueTypeIsString &&
        fieldValue === targetValue &&
        targetCondition === '=' &&
        !value
      ) {
        return {
          field,
          is_passed: false,
          // generic_message: customMessage || `Required, when ${targetFieldUCWordsOnMessage} is equal to ${targetValueOnMessage}`,
          generic_message: 'Required',
          message: `${fieldOnMessage} field is required, when ${targetFieldUCWordsOnMessage} is equal to ${targetValueOnMessage}`,
        };
      }

      if (
        fieldValueTypeIsNumber &&
        fieldValue === +targetValue &&
        targetCondition === '=' &&
        !value
      ) {
        return {
          field,
          is_passed: false,
          // generic_message: customMessage || `Required, when ${targetFieldUCWordsOnMessage} is equal to ${targetValueOnMessage}`,
          generic_message: customMessage || `Required`,
          message: `${fieldOnMessage} field is required, when ${targetFieldUCWordsOnMessage} is equal to ${targetValueOnMessage}`,
        };
      }

      if (
        fieldValueTypeIsString &&
        fieldValue !== targetValue &&
        targetCondition === '!=' &&
        !value
      ) {
        return {
          field,
          is_passed: false,
          generic_message:
            customMessage ||
            `Required, when ${targetFieldUCWordsOnMessage} is equal to ${targetValueOnMessage}`,
          message: `${fieldOnMessage} field is required, when ${targetFieldUCWordsOnMessage} is not equal to ${targetValueOnMessage}`,
        };
      }

      if (
        fieldValueTypeIsNumber &&
        fieldValue !== targetValue &&
        targetCondition === '!=' &&
        !value
      ) {
        return {
          field,
          is_passed: false,
          generic_message:
            customMessage ||
            `Required, when ${targetFieldUCWordsOnMessage} is equal to ${targetValueOnMessage}`,
          message: `${fieldOnMessage} field is required, when ${targetFieldUCWordsOnMessage} is not equal to ${targetValueOnMessage}`,
        };
      }

      if (
        targetValue.split(/,/g).indexOf(fieldValue) > -1 &&
        targetCondition === '~' &&
        !value
      ) {
        return {
          field,
          is_passed: false,
          generic_message:
            customMessage ||
            `Required, when ${targetFieldUCWordsOnMessage} is equal to ${targetValueOnMessage}`,
          message: `${fieldOnMessage} field is required, when ${targetFieldUCWordsOnMessage} is in ${targetValueOnMessage}`,
        };
      }
    }

    if (rule === 'tin_number') {
      const regex = new RegExp(/\d{3,3}-\d{3,3}-\d{3,3}-\d{5,5}/);

      if (value && !regex.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid format, should be 000-000-000-00000',
          message: `${fieldOnMessage} field is invalid format, should be 000-000-000-00000`,
        };
      }
    }

    if (rule === 'mobile_number') {
      const regex = new RegExp(/^09\d{9,9}$/);

      if (value && !regex.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: `Invalid format, should be 09000000000`,
          message: `${fieldOnMessage} field is invalid format, should be 09000000000`,
        };
      }
    }

    if (rule === 'telephone_number') {
      const regex = new RegExp(/^\d{7,7}$/);

      if (value && !regex.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: `Invalid format, should be 0000000`,
          message: `${fieldOnMessage} field is invalid format, should be 0000000`,
        };
      }
    }

    if (rule === 'dti_no') {
      const regex = new RegExp(/^[a-zA-Z]{2}[0-9]{9}$/g);

      if (value && !regex.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: `Invalid format, should be AA000000000`,
          message: `${fieldOnMessage} field is invalid format, should be AA000000000`,
        };
      }
    }

    if (rule === 'sec_no') {
      const regex = new RegExp(/^[a-zA-Z]{2}[0-9]{9}$/g);

      if (value && !regex.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: `Invalid format, should be AA000000000`,
          message: `${fieldOnMessage} field is invalid format, should be AA000000000`,
        };
      }
    }

    if (rule === 'cda_no') {
      const regex = new RegExp(/^[a-zA-Z]{2}[0-9]{9}$/g);

      if (value && !regex.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: `Invalid format, should be AA000000000`,
          message: `${fieldOnMessage} field is invalid format, should be AA000000000`,
        };
      }
    }

    if (rule === 'min_3_digits') {
      if (value && !/^[\d].{2,}$/.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid format, should be minimum of 3 digits',
          message: `${fieldOnMessage} field is invalid format, id length must be greater than 3 digits`,
        };
      }
    }

    if (rule === 'min_5_char') {
      if (value && !/^[\w-].{4,}$/.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid format, should be minimum of 5 characters',
          message: `${fieldOnMessage} field is invalid format, id length must be greater than 5 characters`,
        };
      }
    }

    if (rule === 'max_20_char') {
      // eslint-disable-next-line no-useless-escape
      if (value && !/^[a-zA-Z0-9\-]{1,20}$/.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid format, should be max of 20 characters',
          message: `${fieldOnMessage} field is invalid format, id length must be less than 20 digits`,
        };
      }
    }

    if (rule === 'max_2_char') {
      if (value && !/^[a-zA-Z]{2}$/.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid format, should be 2 characters',
          message: `${fieldOnMessage} field is invalid format, id length must be 2 characters`,
        };
      }
    }

    if (rule === 'max_30_char') {
      if (value && !/^[\w\d].{0,29}$/.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid format, should be max of 30 characters',
          message: `${fieldOnMessage} field is invalid format, id length must be less than 30 characters`,
        };
      }
    }

    if (rule === 'max_12_digits') {
      if (value && !/^[\d].{0,11}$/.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid format, should be max of 12 digit',
          message: `${fieldOnMessage} field is invalid format, id length must be max of 12 digits`,
        };
      }
    }

    if (rule === 'cellphone_number') {
      const regex = new RegExp(/^63\d{10,10}$/);

      if (value && !regex.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: `Invalid format, should be 63900000000`,
          message: `${fieldOnMessage} field is invalid format, should be 639000000000`,
        };
      }
    }

    if (rule === 'cellphone_number_+639') {
      const regex = new RegExp(/^\+639\d{9,9}$/);

      if (value && !regex.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: `Invalid format, should be +63900000000`,
          message: `${fieldOnMessage} field is invalid format, should be +639000000000`,
        };
      }
    }

    if (rule === 'tel_number') {
      const regex = new RegExp(/\d{2,2}-\d{4,4}-\d{4,4}/);

      if (value && !regex.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid format, should be 00-0000-0000',
          message: `${fieldOnMessage} field is invalid format, should be 00-0000-0000`,
        };
      }
    }

    if (rule === 'amount_zero') {
      if (value && +value > MAX_AMOUNT) {
        return {
          field,
          is_passed: false,
          generic_message: `Invalid Amount, should be between 0.00 to ${parseFloat(
            MAX_AMOUNT
          ).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
        };
      }
    }

    if (rule === 'less_than_1') {
      if (value && +value > 0.999999999999) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid Amount, should be less than 1',
          message: `${fieldOnMessage} field is invalid format, should be number`,
        };
      }
    }

    if (rule === 'text_underscore') {
      if (value && !/^[a-z_]*$/.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid format, should be text and underscore only',
          message: `${fieldOnMessage} field is invalid format.`,
        };
      }
    }

    if (rule === 'match') {
      if (value !== data[args]) {
        return {
          field,
          is_passed: false,
          generic_message: `${args} and ${fieldOnMessage} should match`,
          message: `${args} and ${fieldOnMessage} should match`,
        };
      }
    }

    if (rule === 'password') {
      if (
        value &&
        !/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$ %^&*-]).{8,}$/.test(
          value
        )
      ) {
        return {
          field,
          is_passed: false,
          generic_message:
            'Must have minimum of 8 characters, 1 uppercase, 1 lowercase, 1 symbol, and 1 number',
          message: `${fieldOnMessage} is Invalid`,
        };
      }
    }

    if (rule === 'mask') {
      const [regex, mask] = args.split('~');
      const regexp = new RegExp(regex);

      if (value && !regexp.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: `Invalid format, should be ${mask}`,
          message: `${fieldOnMessage} field is invalid format, should be ${mask}`,
        };
      }
    }

    if (rule === 'tin_no') {
      const regex = new RegExp(/\d{3,3}-\d{3,3}-\d{3,3}-\d{3,3}/);

      if (value && !regex.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid format, should be 000-000-000-000',
          message: `${fieldOnMessage} field is invalid format, should be 000-000-000-000`,
        };
      }
    }

    if (rule === 'tdn_no' || rule === 'pin_no') {
      const regex = new RegExp(/\d{3,3}-\d{3,3}-\d{3,3}-\d{3,3}$/);

      if (value && !regex.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: `Invalid format, should be 000-000-000-000`,
          message: `${fieldOnMessage} field is invalid format, should be 000-000-000-000`,
        };
      }
    }

    // if (rule === 'pin_no') {
    //   const regex = new RegExp(/\d{3,3}-\d{3,3}-\d{3,3}-\d{3,3}/);

    //   if (value && !regex.test(value)) {
    //     return {
    //       field,
    //       is_passed: false,
    //       generic_message: 'Invalid format, should be 000-000-000-000',
    //       message: `${fieldOnMessage} field is invalid format, should be 000-000-000-000`,
    //     };
    //   }
    // }

    if (rule === 'pin') {
      const regex = new RegExp(/^[0-9]{4}$/g);

      if (value && !regex.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Must have 4 numbers',
          message: `${fieldOnMessage} is Invalid`,
        };
      }
    }

    if (rule === 'max_20_alphanumeric') {
      const regex = new RegExp(/^[a-zA-Z0-9-]{1,20}$/g);

      if (value && !regex.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: `Invalid format, should be alphanumeric and max of 20 characters`,
          message: `${fieldOnMessage} field is invalid format, should be alphanumeric and max of 20 characters`,
        };
      }
    }

    if (rule === 'min_5_alphanumeric') {
      const regex = new RegExp(/^[\d\w-].{4,}$/g);

      if (value && !regex.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: `Invalid format, should be alphanumeric and minimun of 5 characters`,
          message: `${fieldOnMessage} field is invalid format, should be alphanumeric and minimum of 5 characters`,
        };
      }
    }

    if (rule === 'min_3_character') {
      const regex = new RegExp(/^[\d\w].{2,}$/g);

      if (value && !regex.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: `Invalid format, should be minimun of 3 characters`,
          message: `${fieldOnMessage} field is invalid format, should be minimum of 3 characters`,
        };
      }
    }

    if (rule === 'ovr_number') {
      const regex = new RegExp(/\w{2,2}-\d{7,7}/);

      if (value && !regex.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid format, should be AA-0000000',
          message: `${fieldOnMessage} field is invalid format, should be AA-0000000`,
        };
      }
    }

    if (rule === 'driver_license_number') {
      const regex = new RegExp(/\w{1,1}-\d{2,2}-\d{2,2}-\d{5,5}/);

      if (value && !regex.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: 'Invalid format, should be A-00-00-00000',
          message: `${fieldOnMessage} field is invalid format, should be A-00-00-00000`,
        };
      }
    }

    if (rule === 'zip_code') {
      const regex = new RegExp(/\d{4}$/);
      if (value === '____') {
        return {
          field,
          is_passed: false,
          generic_message: 'Required',
          message: `${fieldOnMessage} field is invalid format, should be 0000`,
        };
      }
      if (value && !regex.test(value)) {
        return {
          field,
          is_passed: false,
          generic_message: `Invalid format`,
          message: `${fieldOnMessage} field is invalid format, should be 0000`,
        };
      }
    }

    return {
      field,
      is_passed: true,
    };
  };

  const process = () => {
    const validateRuleResponse = ruleEntries.map((ruleEntry) => {
      const [field, perItemArrayRules] = ruleEntry;

      const perItemArrayRulesArray = perItemArrayRules.split(/\|/g);

      return perItemArrayRulesArray.map((ruleItem) =>
        validateRule(field, ruleItem)
      );
    });

    const is_passed = validateRuleResponse
      .map((ruleResponse) => ruleResponse.every((item) => item.is_passed))
      .every((isPassed) => isPassed);

    const responseData = validateRuleResponse.reduce(
      (accumulator, currentValue, i) => {
        const checkIsPassed = currentValue
          .map((curr) => curr.is_passed)
          .every((isPassed) => isPassed);

        return {
          ...accumulator,
          [ruleEntries[i][0]]: {
            is_passed: checkIsPassed,
            generic_message: !checkIsPassed
              ? currentValue
                  .filter((is_null) => is_null.generic_message)
                  .map((curr) => curr.generic_message)
              : [],
            message: !checkIsPassed
              ? currentValue
                  .filter((is_null) => is_null.message)
                  .map((curr) => curr.message)
              : [],
          },
        };
      },
      {}
    );
    return {
      is_passed,
      data: responseData,
    };
  };

  const showFirstErrorMessage = () => {
    const responceProcess = process();

    const firstMessage = Object.entries(responceProcess.data)
      .filter((item) => !item[1].is_passed)
      .filter((x, i) => i === 0)
      .reduce((x, current) => current[1].message, true);

    return firstMessage === true ? true : firstMessage[0];
  };

  const validate = () => {
    setResponse(process());
  };

  const renderMessage = (field) => {
    // const rules = object_rules[field] || []

    // const hasRequired = rules.split(/\|/g).indexOf('required') > -1
    // const value = data[field]

    if (!_.isEmpty(response)) {
      return response.data[field] && !response.data[field].is_passed ? (
        <small
          className=" text-danger"
          style={{ fontSize: 10, padding: '1px' }}
        >
          {response.data[field].generic_message[0]}
        </small>
      ) : (
        <small
          className="fa fa-check text-success"
          style={{ fontSize: 10, padding: '1px' }}
        />
      );
    }

    return null;
  };

  return {
    response,
    validate,
    showFirstErrorMessage,
    renderMessage,
    onSetObjectRules,
  };
};
